import { z } from "zod";
import {
  CORRECTION_REASON_MAX,
  businessIdempotencyKeySchema,
  correctionResponseSchema,
  positiveMinorString,
} from "./ledger-correction.js";

/**
 * 🔴 US-AE4.F7-1007 (@UC9951 · QĐ người dùng 05/10/2026) — "SỬA THU": SUPER_ADMIN sửa SỐ TIỀN của MỘT lần ghi tiền
 * vào quỹ đang hiệu lực, hộ người dùng. `POST /v1/admin/funds/:id/transactions/:txId/amount-correction`.
 *
 * Đường sửa là `CorrectionService.correct()` CỦA APP (đảo + ghi lại số đúng trong một giao dịch, khoản đóng tính lại
 * theo sổ) — hợp đồng này chỉ là cửa vào của lane admin, KHÔNG phải engine thứ hai. Vì thế response TÁI DÙNG nguyên
 * `correctionResponseSchema`, và body chỉ có SỐ TIỀN: admin không đổi người nhận (`.strict()` chặn `payeeMemberId`).
 */

/**
 * Loại lần ghi được sửa — closed-set. `CONTRIBUTION` = lần đóng quỹ (gắn `member_dues`), `SPONSORSHIP_MEMBER` = đóng
 * góp khác (dòng "income.recorded" của Sổ quỹ). 🔴 `PAY_ON_BEHALF` CỐ Ý VẮNG: nộp hộ kéo theo nợ người↔người, app chưa
 * có luồng đó — ngoài phạm vi (QĐ 05/10).
 */
export const ADMIN_INCOME_CORRECTABLE_TX_TYPES = ["CONTRIBUTION", "SPONSORSHIP_MEMBER"] as const;
export type AdminIncomeCorrectableTxType = (typeof ADMIN_INCOME_CORRECTABLE_TX_TYPES)[number];

/** Lần ghi không thuộc loại sửa được ở đây (nộp hộ, khoản chi, bút toán đảo, …) — 422, 0 bút toán. */
export const ADMIN_INCOME_CORRECTION_UNSUPPORTED_CODE =
  "ADMIN_INCOME_CORRECTION_UNSUPPORTED" as const;

/**
 * Số mới TRÙNG số đang ghi — 422, 0 bút toán. Không chặn thì máy vẫn đảo rồi ghi lại đúng số cũ: hai bút toán không đổi
 * gì nằm vĩnh viễn trong sổ append-only, và lịch sử sửa có một dòng "sửa" mà không ai sửa gì.
 */
export const ADMIN_INCOME_CORRECTION_UNCHANGED_CODE = "ADMIN_INCOME_CORRECTION_UNCHANGED" as const;

export const adminIncomeCorrectionRequestSchema = z
  .object({
    /** Số ĐÚNG, string minor > 0. */
    amountMinor: positiveMinorString,
    /** Bắt buộc — trim trước khi đo: chuỗi toàn khoảng trắng không phải một lý do. */
    reason: z.string().trim().min(1, "Lý do chỉnh sửa là bắt buộc").max(CORRECTION_REASON_MAX),
    /** Khoá tất định theo NỘI DUNG (client băm) — gửi lại cùng khoá ⇒ đúng MỘT lần sửa. */
    correctionKey: businessIdempotencyKeySchema,
  })
  .strict();
export type AdminIncomeCorrectionRequest = z.infer<typeof adminIncomeCorrectionRequestSchema>;

export const adminIncomeCorrectionResponseSchema = correctionResponseSchema;
export type AdminIncomeCorrectionResponse = z.infer<typeof adminIncomeCorrectionResponseSchema>;
